import { StarPicker, StarRating } from './StarRating';

// `label` / `negLabel` are kept for backward compatibility with old reviews
// that were stored as thumbs. `starLabel` is the neutral, scale-friendly
// label used when the category is rated 1-5 stars (the new model).
// `rent_again` is the only category that stays thumbs-up/down.

export const MANAGER_CATEGORIES = [
  { id: 'maintenance',   label: 'Fast maintenance response',  starLabel: 'Maintenance response',  negLabel: 'Slow or ignored maintenance', icon: '🔧', negIcon: '🔧' },
  { id: 'professional',  label: 'Fair and professional',      starLabel: 'Professionalism',       negLabel: 'Unprofessional behavior',     icon: '🤝', negIcon: '⚠️' },
  { id: 'deposit',       label: 'Returned deposit in full',   starLabel: 'Deposit handling',      negLabel: 'Withheld deposit unfairly',   icon: '💰', negIcon: '💰' },
  { id: 'communication', label: 'Respectful and communicative', starLabel: 'Communication',       negLabel: 'Poor communication',          icon: '💬', negIcon: '💬' },
  { id: 'rent_again',    label: 'Would rent again',           starLabel: 'Would rent again',      negLabel: 'Would NOT rent again',        icon: '✅', negIcon: '🚫' },
];

export const TENANT_CATEGORIES = [
  { id: 'pays_on_time',  label: 'Pays rent on time',          starLabel: 'Pays rent on time',     negLabel: 'Late on rent',                icon: '💵', negIcon: '💵' },
  { id: 'clean',         label: 'Keeps unit clean',           starLabel: 'Cleanliness',           negLabel: 'Left property damaged or dirty', icon: '🧹', negIcon: '🏚️' },
  { id: 'neighbors',     label: 'Respectful to neighbors',    starLabel: 'Respect for neighbors', negLabel: 'Noise or neighbor complaints',icon: '🏘️', negIcon: '🔊' },
  { id: 'communication', label: 'Good communication',         starLabel: 'Communication',         negLabel: 'Difficult to communicate',     icon: '💬', negIcon: '💬' },
  { id: 'rent_again',    label: 'Would rent again',           starLabel: 'Would rent again',      negLabel: 'Would NOT rent again',        icon: '✅', negIcon: '🚫' },
];

/**
 * Per-category 1-5 star picker. No comment field — the per-question
 * textarea has been retired. Used for every category EXCEPT `rent_again`.
 */
export function StarVoting({ categories, values, onChange, missing }) {
  const setRating = (id, rating) => {
    onChange({ ...values, [id]: rating });
  };
  const missingSet = missing instanceof Set ? missing : new Set();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {categories.map((cat) => {
        const current = typeof values[cat.id] === 'number' ? values[cat.id] : 0;
        const isMissing = missingSet.has(cat.id);
        return (
          <div key={cat.id}>
            <div style={{
              background: '#FAF6EC',
              borderRadius: 12,
              padding: '12px 14px',
              border: `1.5px solid ${isMissing ? '#C0392B' : current > 0 ? '#C9972A' : 'rgba(184,137,58,0.22)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              transition: 'all 0.15s',
            }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#343A40', minWidth: 0, flex: '1 1 50%' }}>
                <span style={{ marginRight: 8 }}>{cat.icon}</span>
                {cat.starLabel || cat.label} <span style={{ color: '#C0392B' }}>*</span>
              </div>
              <StarPicker value={current} onChange={(v) => setRating(cat.id, v)} />
            </div>
            {isMissing && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#C0392B', display: 'flex', alignItems: 'center', gap: 6 }}>
                ⚠️ Please rate this category before submitting.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Thumbs up/down voting — now used ONLY for the final `rent_again` question.
 * The comment textarea is no longer rendered (props removed from
 * WriteReview); kept conditional here in case anything else still uses it.
 */
export function ThumbsVoting({ categories, values, onChange, comments, onCommentChange, missing }) {
  const missingSet = missing instanceof Set ? missing : new Set();
  const toggle = (id, isPositive) => {
    const current = values[id];
    if (current === (isPositive ? 'up' : 'down')) {
      onChange({ ...values, [id]: null });
    } else {
      onChange({ ...values, [id]: isPositive ? 'up' : 'down' });
    }
  };

  const showComments = typeof onCommentChange === 'function';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {categories.map((cat) => {
        const val = values[cat.id];
        const isUp = val === 'up';
        const isDown = val === 'down';
        const activeLabel = isDown ? cat.negLabel : cat.label;
        const activeIcon = isDown ? cat.negIcon : cat.icon;

        const selected = isUp || isDown;
        const isMissing = missingSet.has(cat.id);
        return (
          <div key={cat.id}>
          <div style={{
            background: '#FAF6EC',
            borderRadius: 12, padding: '12px 14px',
            border: `1.5px solid ${isMissing ? '#C0392B' : selected ? '#C9972A' : 'rgba(184,137,58,0.22)'}`,
            transition: 'all 0.15s'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#343A40', flex: 1 }}>
                <span style={{ marginRight: 8 }}>{activeIcon}</span>
                {activeLabel} <span style={{ color: '#C0392B' }}>*</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button type="button" onClick={() => toggle(cat.id, true)} style={{
                  width: 40, height: 40, borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 18,
                  background: isUp ? '#C9972A' : '#fff',
                  boxShadow: isUp ? 'none' : '0 1px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.15s',
                }}>👍</button>
                <button type="button" onClick={() => toggle(cat.id, false)} style={{
                  width: 40, height: 40, borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 18,
                  background: isDown ? '#C9972A' : '#fff',
                  boxShadow: isDown ? 'none' : '0 1px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.15s',
                }}>👎</button>
              </div>
            </div>
            {showComments && val && (
              <div style={{ marginTop: 10 }}>
                <textarea
                  value={comments?.[cat.id] || ''}
                  onChange={e => onCommentChange(cat.id, e.target.value)}
                  placeholder={`Why did you choose ${isUp ? '👍' : '👎'} for "${activeLabel}"? (optional)`}
                  rows={2}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 8,
                    border: '1px solid #C9972A',
                    background: 'rgba(201,151,42,0.06)',
                    fontSize: 12, color: '#343A40', outline: 'none', resize: 'vertical',
                    fontFamily: 'inherit', lineHeight: 1.5,
                  }}
                />
              </div>
            )}
          </div>
          {isMissing && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#C0392B', display: 'flex', alignItems: 'center', gap: 6 }}>
              ⚠️ Please answer this question before submitting.
            </div>
          )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Summary chips for a stored review. Renders three forms gracefully:
 *  - Numeric vote (1-5)  → star strip + neutral label   (new model)
 *  - 'up' / 'down' vote  → thumb + positive/negative label (rent_again, old reviews)
 * Old reviews' `comments` map is still shown beneath if present.
 */
export function ThumbsSummary({ votes, comments, profileType }) {
  if (!votes || Object.keys(votes).length === 0) return null;
  const categories = profileType === 'manager' ? MANAGER_CATEGORIES : TENANT_CATEGORIES;
  const voted = categories.filter(c => votes[c.id] !== undefined && votes[c.id] !== null);
  if (voted.length === 0) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {voted.map(cat => {
          const val = votes[cat.id];
          if (typeof val === 'number') {
            return (
              <span key={cat.id} style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 500,
                background: '#FDF6E7',
                color: '#0F2C4E',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <span>{cat.starLabel || cat.label}</span>
                <StarRating rating={val} size={10} />
              </span>
            );
          }
          const isUp = val === 'up';
          return (
            <span key={cat.id} style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 500,
              background: isUp ? '#E8F5EE' : '#FDEDEB',
              color: isUp ? '#1B7A4B' : '#C0392B',
            }}>
              {isUp ? '👍' : '👎'} {isUp ? cat.label : cat.negLabel}
            </span>
          );
        })}
      </div>
      {voted.map(cat => {
        const comment = comments?.[cat.id];
        if (!comment) return null;
        const val = votes[cat.id];
        const isUp = val === 'up' || (typeof val === 'number' && val >= 4);
        return (
          <div key={cat.id} style={{ fontSize: 12, color: '#6C757D', fontStyle: 'italic', marginBottom: 4, paddingLeft: 8, borderLeft: `2px solid ${isUp ? '#1B7A4B' : '#C0392B'}` }}>
            <strong style={{ color: isUp ? '#1B7A4B' : '#C0392B', fontStyle: 'normal' }}>
              {cat.starLabel || cat.label}:
            </strong>{' '}
            "{comment}"
          </div>
        );
      })}
    </div>
  );
}
